import React, { useEffect, useMemo, useState } from 'react';
import cx from "classnames";
import CmsContentList from "components/data/CmsContentList";

import TakeOverLayout from 'layouts/TakeOverLayout.js';
import useFormHelper from 'utils/form-helper/useFormHelper.js';
import SelectPaymentMethodForm from 'components/account/card/add-passes/SelectPaymentMethodForm.js';
import { getCartSubtotal, getCartTotal, useCartContext } from 'context/CartProvider.js';
import { Redirect } from "react-router-dom";
import PreventDefault from 'utils/PreventDefault.js';
import routeKeys from 'CustomerRouteKeys.js';
import { useGlobalToastsContext } from 'context/ToastProvider.js';
import { getPathByRoute } from 'App.js';
import {
	getYupSchema as getCreditCardFormYupSchema,
	removeAddressFormInputPrefix,
} from 'pages/account/purchase/AddPaymentMethod.js';
import FundingSourcesProvider, { useFundingSourcesContext } from 'context/FundingSourcesContext.js';
import {
	NEW_ADDRESS_ID,
	AddressSelectorContext,
	useAddressSelectorState,
} from 'components/account-settings/address-selector/AddressSelector.js';
import Addresses from 'components/data/session-user/Addresses.query.js';
import SelectAddressForm, { AddressType } from 'components/account-settings/SelectAddressForm.js';
import CmsContentRenderer from 'components/data/CmsContentRenderer.js';
import Toast, { TYPE_ERROR } from 'components/Toast';
import CmsContentRendered from 'components/data/CmsContentRendered.js';
import { CartStatic } from 'components/payments/Cart.js';
import { Lifecycles } from 'libreact/lib/Lifecycles/index.js';
import LoadingIcon from 'components/icons/LoadingIcon.js';
import { GET_TRANSIT_ACCOUNT, getTransitAccountDefaultRefetchVariables, useTransitAccount } from 'components/data/transit-account/TransitAccount.js';

import Button, { buttonTypeStylePlain } from 'components/Button.js';
import { useTransitAccountIdContext } from 'context/TransitAccountIdContext.js';
import useStdQuery from 'components/data/hooks/useStdQuery.js';
import { GET_FULLNAME } from 'components/data/session-user/SessionUser.js';

import { getAddressYupSchema } from 'components/forms/address/Address.validations.js';
import { getPrimaryPaymentMethodSchema } from 'pages/account/purchase/PurchaseProductPayment.js';
import { levels, noticeError } from 'utils/Logger.js';
import WSName from 'server/api-types/WSName.js';
import WSAddressExt from 'server/api-types/WSAddressExt.js';
import WSIssueMediaLineItem from 'server/api-types/WSIssueMediaLineItem.js';
import WSIssueSubsystemMedia from 'server/api-types/WSIssueSubsystemMedia.js';
import { OPEN_TRANSIT_REGULAR_CARD } from 'server/api-types/WSSubsystemAccountToken.js';
import { useApolloClient } from '@apollo/client';
import WSAddress from 'server/api-types/WSAddress.js';
import FormHelperProvider from 'utils/form-helper/FormHelperProvider.js';
import PurseBalanceProvider, { usePurseBalanceContext } from 'context/PurseBalanceContext.js';
import { ORDER_REPLACE_MUTATION } from 'components/data/order/replace/OrderReplace.mutation.js';
import { graphqlErrorMiddleware } from 'utils/error-handling/graphql/GraphqlClientMiddleware.js';
import PublicAppVars from 'utils/PublicAppVars.js';
import { SAVE_ADDRESS_SHOWN } from "components/payments/AddressForm.js";
import { postNewAddress } from "components/account/address/PostNewAddress.js";
import { getPayments } from "utils/payment/getPayments.js";
import { useFinalizeAndSetFundingSourcesToContext } from "pages/account/purchase/hooks/FinalizeFundingSourcesFromForm.js";

import * as tabs from 'styles/Tabs.module.css';
import * as btnStyles from "components/Button.module.css";
import { findPrimaryToken } from 'components/manage-cards/TokenHelpers';
import { SUBSYSTEM_ID } from 'utils/Constants';
import { useReplacementOptions } from 'components/data/transit-account/ReplacementOptions.query';
import useStandardMutation from 'components/data/hooks/useStandardMutation';
import { GET_REPLACE_PAYMENT_METHODS } from 'components/data/order/replace/Paymentmethods.query';
import { useReasonCodeFees } from 'components/data/transit-account/ReasonCodeFees';
import { useTransUnionContext, setFraudDetectionAdBlockerToast } from 'context/TransUnionContext';
import { useLoginStage } from 'components/data/session-user/LoggingIn';

const cms = {
	header: 'miscText.card-upgrade-header',
	shippingOnly: 'miscText.card-upgrade-shipping-only',
	shippingBilling: 'miscText.card-upgrade-shipping-billing',
	paymentSelectSubheader: 'miscText.general-payment-select-subheader',
	shippingSelectSubheader: 'miscText.card-upgrade-shipping-select-subheader',
	submitBtn: 'miscText.card-upgrade-submit',
	enablementFeeInfo: 'miscText.card-upgrade-cart-enablement-fee',
	enablementFeeInfoBtn: 'miscText.card-upgrade-cart-enablement-fee-cta',
	enablementFeeInfoUrl: 'miscText.card-upgrade-cart-enablement-fee-url',
	confirmation: 'miscText.card-upgrade-confirmation',
	invalidMediaProductToast: 'miscText.invalid-media-product-toast-header',
};

// ReplacementTypes supported by 2-2-754. order/replace POST API
const upgradeTransitAccountMedia = 'UpgradeTransitAccountMedia';

const getReplacementFundingSources = async ({ getReplacePaymentMethods, saveShoppingCartId, initializeFundingSources, riderClass, primaryToken = {}, subsystemAccountReference, charlieCard = {} }) => {
	const paymentMethodsRes = await getReplacePaymentMethods({
		variables: {
			issueMediaLineItems: [
				new WSIssueMediaLineItem({
					issueMedia: new WSIssueSubsystemMedia({
						...charlieCard,
						subsystem: SUBSYSTEM_ID,
						type: 'TransitAccountMedia',
						subsystemAccountReference,
						replacedTravelTokenId: primaryToken.tokenId,
						replacementFeeAmount: charlieCard.replacementFeeAmount,
						riderClassId: riderClass,
						itemTotalAmount: charlieCard.replacementFeeAmount,
						mediaType: OPEN_TRANSIT_REGULAR_CARD,
						waiveEnableFeeFlag: false,
						replacedTokenReasonCode: "755",
					}),
				}).toResolver(),
			],
		},
		skip: !riderClass || !primaryToken || charlieCard,
	});

	const {
		shoppingCartId,
		loadProductLineItemFunds,
		fundingSources,
		restrictFunding,
	} = paymentMethodsRes.data.OrderRoute.getReplacePaymentmethods;

	saveShoppingCartId(shoppingCartId);
	initializeFundingSources({
		loadProductLineItemFunds,
		fundingSources, // might be empty
		restrictFunding,
		refetchFundingSources: async () => (await getReplacePaymentMethods()).data.OrderRoute.getReplacePaymentmethods,
	});
};

// Per Patrick this is the reason code that should always be used
const DEFAULT_REASON_CODE = '755';

const UpgradeCardPage = () => {
	const subsystemAccountReference = useTransitAccountIdContext();
	const [ redirect, setRedirect ] = useState(null);
	const [ isInvalidMediaProduct, setIsInvalidMediaProduct ] = useState(false);
	const { data: { session: { customerId } } } = useLoginStage();
	const { setToast, removeToast } = useGlobalToastsContext();
	const { data: nameData } = useStdQuery(GET_FULLNAME);
	const { cart, addUnpaidFee, setNegativeBalance, addProduct, addBalance } = useCartContext();
	const addressSelectorState = useAddressSelectorState({ addressType: AddressType.Shipping });
	const apolloClient = useApolloClient();
	const { saveShoppingCartId } = useCartContext();
	const [ getReplacePaymentMethods ] = useStandardMutation(GET_REPLACE_PAYMENT_METHODS);
	const fingerprint = useTransUnionContext();

	const {
		data: transitAccountData,
		loading: transitAccountLoading,
	} = useTransitAccount({ subsystemAccountReference });

	const { riderClass, tokens } = transitAccountData?.transitAccountQ ?? {};
	const primaryToken = findPrimaryToken(tokens);
	const successCloseRoute = getPathByRoute(routeKeys.AccountCardOverview, { transit_account_id: subsystemAccountReference });
	const { mediaProducts, queryResult } = useReplacementOptions({ tokenId: primaryToken?.tokenId, replacementType: upgradeTransitAccountMedia,  subsystemAccountReference });

	const loadingStandardCharlieCard = queryResult?.loading;

	const {
		initializeFundingSources,
		selectedFundingSources,
		loadProductLineItemFunds,
		isMultiPayment,
	} = useFundingSourcesContext();

	const isNewCard = selectedFundingSources[ 0 ]?.isNew;

	const isSplit = false;
	const isFirst = true;
	const mobilePayment = false;

	// Find and set enablement fee
	const { hasNegativeBalance, purseTotal } = usePurseBalanceContext();

	const charlieCard = useMemo(() =>
		mediaProducts?.find(prod =>
			prod.mediaTypeName === OPEN_TRANSIT_REGULAR_CARD &&
			prod.mediaRestrictions.some(rest => rest.mediaRestrictionName === "Replace")
		) ??
		null
	, [	mediaProducts ]);

	const {	replacementFeeObj } = useReasonCodeFees({
		subsystemAccountReference,
		tokenId: primaryToken?.tokenId,
		reasonCodeId: DEFAULT_REASON_CODE,
		mediaSKU: charlieCard?.mediaSKU,
	});

	const requiresPayment = (replacementFeeObj?.chargeReplacementFee && replacementFeeObj?.replacementFeeAmount > 0) || hasNegativeBalance;

	// User Should not be here if we can not Enable upgrade is disabled.
	useEffect(() => {
		if (!PublicAppVars.ENABLE_UPGRADE_CARD) {
			setRedirect(<Redirect to={{ pathname: getPathByRoute(routeKeys.AccountCardOverview, { transit_account_id: subsystemAccountReference }) }} />);
		}
	}, [ subsystemAccountReference ]);

	// Add the product to the cart on page load
	useEffect(() => {
		if (cart.mediaOptions.length === 0 && charlieCard) {
			addProduct(charlieCard, 1);
		}
	},[ addProduct, charlieCard, cart.mediaOptions, addBalance ]);

	// Give the page time to load a mediaProduct. If there is none the user cannot continue with the flow.
	// This would happen if we have bad BO data
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (!charlieCard) {
				setIsInvalidMediaProduct(true);
				setToast(<Toast
					type={TYPE_ERROR}
					title={<CmsContentRendered.Span
						contentKey={cms.invalidMediaProductToast}
						fallbackValue="Sorry, this feature is currently unavailable for this card."
					/>}
				/>);
			}
		}, 2000);

		return () => clearTimeout(timeoutId);
	}, [ charlieCard, setToast ]);

	// Add a replacementFee even if it's 0 for consistency
	useEffect(() => {
		if (!cart?.unpaidFee
			&& replacementFeeObj?.chargeReplacementFee
			&& replacementFeeObj?.replacementFeeAmount !== undefined
			&& replacementFeeObj?.replacementFeeAmount !== null) {
			addUnpaidFee(replacementFeeObj.replacementFeeAmount);
		}
	}, [ addUnpaidFee, replacementFeeObj, cart ]);

	useEffect(() => {
		if (hasNegativeBalance && !cart?.negativeBalance) {
			setNegativeBalance(purseTotal);
		}
	}, [ hasNegativeBalance, setNegativeBalance, purseTotal, cart ]);

	const total = getCartTotal(cart) ?? 0;

	const formHelperContext = useFormHelper({
		getYupSchema: (formHelper) =>
			// if fee is unpaid, we payment methods and shipping address
			requiresPayment
				? getCreditCardFormYupSchema({
					formHelper,
					isMultiPayment,
					isSelecting: [ false ],
					isAch: false,
					requireAchEula: false,
					isNew: [ isNewCard ],
					total,
					hasMobilePayment: Boolean(mobilePayment),
					disableCvv: total === 0,
				})
					.concat(getPrimaryPaymentMethodSchema())
					.concat(getAddressYupSchema(addressSelectorState.addressType))
				: getAddressYupSchema(addressSelectorState.addressType),
		getDataToValidate: (formHelper) => {
			const data = formHelper.getFormData();
			if (data.expirationDate) {
				data.expirationMonth = parseInt(data.expirationDate.slice(0, 2), 10);
				data.expirationYear = parseInt(data.expirationDate.slice(-2), 10);
			}
			return data;
		},
	});

	const {
		formRef,
		formHelper,
		submitting,
		setSubmitting,
	} = formHelperContext;

	const finalizeAndSetFundingSourcesToContext = useFinalizeAndSetFundingSourcesToContext({ formHelper });

	if (loadingStandardCharlieCard || transitAccountLoading) {
		return <LoadingIcon />;
	}

	if (redirect) {
		return redirect;
	}

	// Form Functions
	const kickoffSubmit = async () => {
		setSubmitting(true);

		try {
			let validated;
			try {
				validated = await formHelper.startValidation(true);
			}
			catch (errorReport) {
				setSubmitting(false);
				noticeError(null, levels.verbose, errorReport, `Upgrade Card Failed`);
				formHelper.validationErrorHandler(errorReport);
				return;
			}

			// We'll validate the fingerprint on submission
			if (!fingerprint && PublicAppVars.TRANS_UNION_BLACKBOX_ENABLED && requiresPayment) {
				setFraudDetectionAdBlockerToast(setToast, removeToast);
				setSubmitting(false);
				return;
			}

			const {
				firstName,
				lastName,
			} = nameData?.session.customer.contact.name;

			const selectedFundingSources = await finalizeAndSetFundingSourcesToContext();

			const payments = requiresPayment
				? getPayments({
					cart,
					selectedFundingSources,
					loadProductLineItemFunds,
					supplementalInfo: fingerprint,
					currentCustomerId: customerId,
				})
				: null;

			const shippingAddress = removeAddressFormInputPrefix(validated, AddressType.Shipping);

			const wsAddressExt = shippingAddress.saveAddress
				? await postNewAddress({
					wsAddress: new WSAddress(shippingAddress),
					apolloClient,
				})
				: new WSAddressExt({
					...shippingAddress,
					...(
						addressSelectorState.selectedAddressId === NEW_ADDRESS_ID
							? {}
							: { ...shippingAddress }
					),
				});

			const replacementFeeAmount = replacementFeeObj.chargeReplacementFee
				? replacementFeeObj.replacementFeeAmount
				: 0;

			const variables = {
				replacementType: upgradeTransitAccountMedia,
				issueMediaLineItems: [
					new WSIssueMediaLineItem({
						issueMedia: new WSIssueSubsystemMedia({
							...charlieCard,
							subsystem: SUBSYSTEM_ID,
							type: 'TransitAccountMedia',
							subsystemAccountReference,
							replacedTravelTokenId: primaryToken.tokenId,
							enablementFeeAmount: 0,
							riderClassId: riderClass,
							itemTotalAmount: replacementFeeAmount,
							mediaType: OPEN_TRANSIT_REGULAR_CARD,
							waiveEnableFeeFlag: false,
							replacedTokenReasonCode: DEFAULT_REASON_CODE,
						}),
					}).toResolver(),
				],
				payments: payments?.map(wsPayment => wsPayment.toInputWSPaymentFactory()),
				itemsSubtotalAmount: getCartSubtotal({ cart }),
				orderTotalAmount: getCartTotal(cart),
				shippingName: new WSName({
					firstName,
					lastName,
				}).toResolver(),
				// Per Jon W: We are correct to use WSAddressExt and yes WSAddressExt.addressId is a required field per API spec
				// but the purchase flow allows for an empty value. jira is ccsa-3849
				// https://reflexions.slack.com/archives/CCF68M49M/p1643996850325519?thread_ts=1643232391.221400&cid=CCF68M49M
				shippingAddress: wsAddressExt,
				shoppingCartId: cart.shoppingCartId,
				reasonCode: DEFAULT_REASON_CODE, // Default to 755 until we provide the user with a reason selection. https://reflexions.slack.com/archives/CCF68M49M/p1746643430750319?thread_ts=1746183609.414339&cid=CCF68M49M
			};

			await graphqlErrorMiddleware(
				apolloClient.mutate({
					mutation: ORDER_REPLACE_MUTATION,
					variables,
					refetchQueries: [ { query: GET_TRANSIT_ACCOUNT, variables: getTransitAccountDefaultRefetchVariables(subsystemAccountReference) } ],
					awaitRefetchQueries: true,
				}),
			);
		} catch (errorReport) {
			noticeError(null, levels.info, errorReport, `Upgrade Card Submit Failed`);
			formHelper.validationErrorHandler(errorReport);
			setSubmitting(false);
			return;
		}

		setToast(<Toast
			type="success"
			title={<CmsContentRendered.Span
				contentKey={cms.confirmation}
				fallbackValue="Your Charlie Card has been upgraded, and your new plastic card will be mailed to the shipping address you provided. Your current card will be usable until the new card is activated. Once activated, the new card will replace the current one."
			/>}
			onClosed={removeToast}
		/>);

		setRedirect(<Redirect push to={{ pathname: successCloseRoute }} />);
	};

	return (
		<Lifecycles didMount={() => requiresPayment
			? getReplacementFundingSources({ getReplacePaymentMethods, saveShoppingCartId, initializeFundingSources, riderClass, primaryToken, subsystemAccountReference, charlieCard })
			: !!charlieCard?.mediaSKU
		}>
			<CmsContentList list={Object.values(cms)}>{() =>
				<FormHelperProvider {...{ formHelperContext }}>
					<form
						method="post"
						ref={formRef}
						onSubmit={PreventDefault(kickoffSubmit)}
					>
						<TakeOverLayout
							title={<CmsContentRenderer.Span
								contentKey={cms.header}
								fallbackValue="Upgrade to Standard Fare Card"
							/>}
							showCancel
							cancelLink={successCloseRoute}
						>
							<div className={tabs.mainContent}>
								<div className={cx(tabs.main)}>
									<CmsContentRenderer.H2
										contentKey={cms.shippingBilling}
									/>
									<div className={tabs.creditCardFormContainer}>
										{formHelper.getFieldErrorJsx('')}
										{requiresPayment &&	<>
											<SelectPaymentMethodForm {...{
												provideNickName: false,
												formHelper,
												isSplit: false,
											}} />
										</>}
										<div>
											<CmsContentRenderer.H3
												className={tabs.title}
												contentKey={cms.shippingSelectSubheader}
												fallbackValue="Where should your upgraded Charlie Card be shipped?"
											/>
											{addressSelectorState ?
												<AddressSelectorContext.Provider value={addressSelectorState}>
													<Addresses>{(wsAddressExts) =>
														<SelectAddressForm
															{...{
																formHelper,
																wsAddressExts,
																isSplit,
																isFirst,
																saveAddressCheckboxVisibility: SAVE_ADDRESS_SHOWN,
															}}
														/>
													}</Addresses>
												</AddressSelectorContext.Provider>
												: null
											}
										</div>
									</div>
									<Button
										isPrimary
										type="submit"
										disabled={isInvalidMediaProduct}
										submitting={submitting}>
										<CmsContentRenderer.Span contentKey={cms.submitBtn} fallbackValue="Confirm your order" />
									</Button>
								</div>

								<div className={tabs.sidebar}>
									<CmsContentRenderer.Div
										contentKey={cms.enablementFeeInfo}
										fallbackValue="You will need to pay a replacement fee to add this new card to your account. You will also need to pay for shipping."
									/>
									<Button
										overrideClass={btnStyles.learnMorePolicy}
										typeStyle={buttonTypeStylePlain}
										to={cms.enablementFeeInfoUrl}
										external={true}
									>
										<CmsContentRenderer.Span
											contentKey={cms.enablementFeeInfoBtn}
											fallbackValue="Learn more about the policy."
										/>
									</Button>
								</div>
							</div>
						</TakeOverLayout>

						{/* No need to show the cart if there is nothing to be purchase */}
						{replacementFeeObj?.chargeReplacementFee &&
				 			<CartStatic {...{ formHelper, submitting, disableNext: true, amountEditable:false }} />
						}
					</form>
				</FormHelperProvider>
			}</CmsContentList>
		</Lifecycles>
	);
};

const UpgradeCard = () =>
	<FundingSourcesProvider>
		<PurseBalanceProvider>
			<UpgradeCardPage />
		</PurseBalanceProvider>
	</FundingSourcesProvider>;


export default UpgradeCard;
